import {
  BillingApi,
  BillingProduct,
  BillingProductList,
  CurrencyCode,
  ErrorResponse,
} from '@citruscamps/citrus-client'
import { useQuery } from 'react-query'
import { DefaultMainPagination } from '../../../../../constants/pagination'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateListKey } from '../../../../../utils/key-generator'

const DefaultFetchProps: FetchProps = {
  sort: 'created',
  order: 'DESC',
  filter: {
    currency: 'cad',
  },
}

interface FetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: {
    currency: CurrencyCode
  }
}

interface IProps {
  pagination?: Pagination
  fetchProps?: FetchProps
}

interface FetchBillingPlans {
  data: BillingProduct[]
  error: ErrorResponse | null
  isError: boolean
  isLoading: boolean
  pagination: Pagination
}

export const useFetchBillingPlans = ({
  pagination = DefaultMainPagination,
  fetchProps = DefaultFetchProps,
}: IProps = {}): FetchBillingPlans => {
  const { requestHandler } = useRequestHandler()
  const queryKeys = generateListKey({
    type: 'billing_product',
    program_id: undefined,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<BillingProductList, ErrorResponse>(queryKeys, async ({ signal }) => {
    const client = new BillingApi(generateApiConfig())
    const skip = pagination.limit ? pagination.page * pagination.limit : undefined
    return await requestHandler<BillingProductList>(() => {
      if (!fetchProps.filter.currency) {
        throw new Error('Currency is required')
      }
      return client.findPlansBilling(
        {
          currency: fetchProps.filter.currency,
          limit: pagination.limit,
          skip,
          order: fetchProps.order,
          sort: fetchProps.sort,
        },
        { signal },
      )
    })
  })

  return {
    data: resp?.data || [],
    error,
    isError,
    isLoading,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
  }
}
